import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, Nav, NavDropdown, Container } from "react-bootstrap";
import "../styles/NavBar.css";
// @ts-ignore
import IntellexusLogo from "../assets/InetellexusLogo.png";
import Logout from "./Logout";
import { RoutesLogic } from "../common/entities/routesLogic";
import { SystemEntityCollections } from "../shared/enums/shared-enums";
import { SystemLanguages } from "../shared/enums/shared-enums";

interface DropdownItem {
  label: string;
  language: SystemLanguages;
}

interface DropdownConfig {
  key: string;
  title: string;
  collection: SystemEntityCollections;
  items: DropdownItem[];
}

interface NavLinkConfig {
  label: string;
  collection: SystemEntityCollections;
}

const NavBar = () => {
  const navigate = useNavigate();
  
  // Configuration for dropdown menus
  const dropdownConfigs: DropdownConfig[] = [
    {
      key: "texts",
      title: "Texts",
      collection: SystemEntityCollections.TEXT,
      items: [
        { label: "Indic", language: SystemLanguages.indic },
        { label: "Tibetan", language: SystemLanguages.tibetan },
        { label: "Chinese", language: SystemLanguages.chinese },
      ],
    },
    {
      key: "versions",
      title: "Versions",
      collection: SystemEntityCollections.VERSION,
      items: [
        { label: "Indic", language: SystemLanguages.indic },
        { label: "Tibetan", language: SystemLanguages.tibetan },
        { label: "Chinese", language: SystemLanguages.chinese },
      ],
    },
    {
      key: "works",
      title: "Works",
      collection: SystemEntityCollections.WORK,
      items: [
        { label: "Indic", language: SystemLanguages.indic },
        { label: "Tibetan", language: SystemLanguages.tibetan },
        { label: "Chinese", language: SystemLanguages.chinese },
      ],
    },
    {
      key: "teams",
      title: "Teams",
      collection: SystemEntityCollections.TEAM,
      items: [
          { label: "Tibetan", language: SystemLanguages.tibetan },
        { label: "Chinese", language: SystemLanguages.chinese },
      ],
    },
  ];

  // Configuration for regular nav links
  const navLinkConfigs: NavLinkConfig[] = [
    { label: "Persons", collection: SystemEntityCollections.PERSON },
    { label: "Colophons", collection: SystemEntityCollections.COLOPHON },
    { label: "Bibliographies", collection: SystemEntityCollections.BIBLIOGRAPHY },
    { label: "Places", collection: SystemEntityCollections.PLACE },
    { label: "Codes", collection: SystemEntityCollections.USER_ENUM },
  ];

  // Initialize dropdown states dynamically
  const initialDropdownStates = dropdownConfigs.reduce(
    (acc, config) => ({ ...acc, [config.key]: false }),
    {} as Record<string, boolean>
  );

  const [dropdownStates, setDropdownStates] = useState(initialDropdownStates);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleMouseEnter = (key: string) => {
    setDropdownStates((prevState) => ({ ...prevState, [key]: true }));
  };

  const handleMouseLeave = (key: string) => {
    setDropdownStates((prevState) => ({ ...prevState, [key]: false }));
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsCollapsed(true); // Close the collapse when an option is selected
  };

  return (
    <Navbar
      expand="lg"
      sticky="top"
      className="navbar"
      expanded={!isCollapsed} // Control the collapse state
      onToggle={() => setIsCollapsed(!isCollapsed)} // Toggle collapse state
    >
      <Container>
        {/* Brand/Logo */}
        <div className="d-flex justify-content-between align-items-center w-100">
          <Navbar.Brand
            onClick={() => handleNavigate("/")}
            style={{ cursor: "pointer" }}
          >
            <div className="nav-logo d-flex align-items-center">
              <img
                src={IntellexusLogo}
                alt="Intellexus Logo"
                className="nav-logo-img"
              />
            </div>
          </Navbar.Brand>

          {/* Toggle Button for Small Screens */}
          <Navbar.Toggle
            className="nav-toggle"
            aria-controls="basic-navbar-nav"
          />
        </div>

        {/* Collapsible Navigation Menu */}
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto nav-links">
            {/* Render dropdown menus */}
            {dropdownConfigs.map((config) => (
              <div
                key={config.key}
                className="dropdown-wrapper"
                onMouseEnter={() => handleMouseEnter(config.key)}
                onMouseLeave={() => handleMouseLeave(config.key)}
              >
                <NavDropdown
                  title={config.title}
                  id={`${config.key}-dropdown`}
                  className="nav-item centered-dropdown"
                  show={dropdownStates[config.key]}
                >
                  {config.items.map((item) => (
                    <NavDropdown.Item
                      key={item.language}
                      onClick={() =>
                        handleNavigate(
                          RoutesLogic.getTableRoute(config.collection, item.language)
                        )
                      }
                    >
                      {item.label}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </div>
            ))}

            {/* Render regular nav links */}
            {navLinkConfigs.map((link, index) => (
              // Insert Persons link at the beginning
              index === 0 ? (
                <Nav.Link
                  key={link.collection}
                  onClick={() =>
                    handleNavigate(RoutesLogic.getTableRoute(link.collection))
                  }
                  className="nav-item"
                >
                  {link.label}
                </Nav.Link>
              ) : (
                <Nav.Link
                  key={link.collection}
                  onClick={() =>
                    handleNavigate(RoutesLogic.getTableRoute(link.collection))
                  }
                  className="nav-item"
                >
                  {link.label}
                </Nav.Link>
              )
            ))}

            <Nav.Link
              onClick={() => handleNavigate("/markdown")}
              className="nav-item"
            >
              Info
            </Nav.Link>
            <Logout />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavBar;
